// Debounce (delay) callback fn.
export function debounce(callback, delay, immediate) {
  let timeout;
  return (...args) => {
    const later = () => {
      timeout = null;
      !immediate && callback(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, delay);
    immediate && !timeout && callback(...args);
  };
}

// Extract search query from URL and parse it into JS object.
// Returns: {} OR {key:value, ...};
export function queryToObject(url) {
  const searchParams = new URLSearchParams(url);
  const result = {};
  for (const [key, value] of searchParams.entries()) {
    if (result[key]) {
      if (!Array.isArray(result[key])) {
        result[key] = [result[key]]
      }
      result[key].push(value);
    } else {
      result[key] = value;
    }
  }
  return result;
}


// Update query params.
export function updateQuery(query, paramName, paramValue) {
  const qs = new URLSearchParams(query);

  paramValue === undefined || paramValue === null
    ? qs.delete(paramName)
    : qs.set(paramName, paramValue);

  return decodeURIComponent(qs.toString());
}


// Update Browser history state with new search params.
export function updateHistorySearchState(search) {
  const { pathname } = window.location;
  const url = pathname + "?" + search;
  window.history.replaceState({}, "", url);
}

// Geerate unique id.
export const uid = () =>
  `PIE.${(+new Date() + Math.random() * 100).toString(32)}`;

// Modifier function with memory.
export const memo = (modifier, previous) => {
  if (typeof modifier !== "function") throw "Modifier need to be a function.";
  return current => {
    // NOTE: To memoize previous value you need to return it from the 'modifier'.
    previous = modifier(current, previous);
    return previous;
  };
};

// Memoize prev. value.
export function isPrevious(state) {
  return newState => {
    if (state !== newState) {
      state = newState;
      return false;
    }
    return true;
  };
}

// Translate clock time notation into seconds.
export function timeToSeconds(time) {
  const parts = time.split(":");
  return (
    parseInt(parts[0]) * 60 * 60 + parseInt(parts[1]) * 60 + parseInt(parts[2])
  );
}

// Add <style> tag into Document's head.
export function insertCSS(styles, name, override = false) {
  const styleTag = override
    ? document.querySelector(`style[data-name="${name}"]`) ||
    document.createElement(`style`)
    : document.createElement(`style`);
  if (name) styleTag.dataset.name = name;
  document.head.appendChild(styleTag).textContent = styles;
  return styleTag;
}


// Toggles given @klass on given @newElement removing its from the previouse one.
// USAGE:
// const avtivate = memoElement(htmlElement, "avtive");
// ...
// avtivate(newHtmlElement);
//
export function memoElement(currentElement, klass, toggle = false) {
  currentElement && currentElement.classList.add(klass);
  return newElement => {
    if (toggle && currentElement && currentElement === newElement) {
      currentElement.classList.toggle(klass);
    } else {
      currentElement && currentElement.classList.remove(klass);
      newElement && newElement.classList.add(klass);
      // eslint-disable-next-line no-param-reassign
      currentElement = newElement;
    }
    return currentElement;
  };
}


// Runs callback only id screen mode changes.
// USAGE:
/*

import { watchScreen } from "utils";
. . .

watchScreen(width => ({
  mobile: width < 450,
  tablet: width < 860 && width >= 450,
}),
  (mode, isInitail) => console.log(mode, isInitail),
  true
);

*/


export function watchScreen(predicate, callback, emmitOnInit = false) {

  let prevMode;

  const handler = debounce(() => {
    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight;
    const { mobile, tablet } = predicate(screenWidth, screenHeight);
    const mode = mobile ? "mobile" : tablet ? "tablet" : "desktop";

    if (prevMode !== mode) {
      callback(mode, prevMode === undefined);
      prevMode = mode;
    }

  }, 450);

  emmitOnInit && handler();

  window.addEventListener("resize", handler);
  return () => window.removeEventListener("resize", handler);
}

// Async querySelectorAll with @wait and @retry.
export function asyncSelectror(selector, options = {}) {
  const { wait = 500, retry = 6, justFirst = false } = options;
  return new Promise((resolve, reject) => {
    let attempts = 0;
    const interval = setInterval(() => {
      const elements = Array.from(document.querySelectorAll(selector));
      if (Boolean(elements.length)) {
        clearInterval(interval);
        resolve(justFirst ? elements[0] : elements);
      } else if (attempts > retry) {
        clearInterval(interval);
        reject(new Error(`Element "${selector}" not found`));
      }
      attempts++;
    }, wait);
  });
}

asyncSelectror.first = function (selector) {
  return asyncSelectror(selector, { justFirst: true });
}

// Create UI for pie-loader element.
export function pieLoader(classes = "") {
  const loader = document.createElement("span");
  loader.className = `pie-loader${classes.length ? " " + classes : ""}`;
  loader.innerHTML = `<span></span><span></span><span></span><span></span>`;
  return loader;
}


// Promisified setTimeout.
export function wait(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

// Parse text with markdown links into HTML.
export function mdWithLinks(md) {
  return md.replace(/\[([^\]]+)\]\(([^)]+)\)/g, `<a class="pie-link violet" target="_blank" rel="noopener noreferer" href="$2">$1</a>`);
}

export function stripHTMLTags(str) {
  return str.replace(/(<([^>]+)>)/gi, "");
}

export function analytics(event, params = {}) {
  window.dataLayer?.push({ event, ...params });
};